<script lang="ts">
  import { onMount, tick } from 'svelte'
  import { highlightAll } from '@/utils/highlight'

  // Rotating marketing taglines for the page byline. Cycles every
  // BYLINE_ROTATION_MS unless the user prefers reduced motion, in which
  // case the first one is shown statically.
  const BYLINES = [
    {
      hook: 'Your audience hears it from you first.',
      body: 'Triggers draft the SMS the moment something happens — you review and send.'
    },
    {
      hook: 'When the moment hits, your message is already written.',
      body: 'Triggers turn weather alerts, forwarded emails, and your own system events into ready-to-send SMS — you review, tap send, done.'
    },
    {
      hook: 'Be first when it matters.',
      body: 'Triggers watch the events you care about and draft the SMS the moment something happens — review and send in seconds.'
    },
    {
      hook: "Don't write the message twice.",
      body: 'Triggers draft your SMS from weather alerts, forwarded emails, and your own systems — you review and send.'
    }
  ]
  const BYLINE_ROTATION_MS = 30_000

  interface Props {
    inboundEmailDomain?: string
  }

  let { inboundEmailDomain = '' }: Props = $props()

  interface TriggerSummary {
    id: string
    name: string
    source_type: string
    target_group_names: string[]
    target_group_count: number
    reviewer_email: string | null
    active: boolean
    created_at: string
    recent_fire_count: number
    last_fire_at: string | null
  }

  function formatGroupList(names: string[]): string {
    if (!names || names.length === 0) return '—'
    if (names.length <= 2) return names.join(', ')
    return `${names[0]}, ${names[1]}, +${names.length - 2} more`
  }

  function timeAgo(iso: string | null): string {
    if (!iso) return '—'
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) return '—'
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
    if (diffSec < 60)        return `${diffSec}s ago`
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60)        return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24)         return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 30)        return `${diffDay}d ago`
    const diffMo = Math.floor(diffDay / 30)
    if (diffMo < 12)         return `${diffMo}mo ago`
    return `${Math.floor(diffDay / 365)}y ago`
  }

  let triggers = $state<TriggerSummary[]>([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let explainerOpen = $state(true)
  let explainerInitialized = false
  let bylineIndex = $state(0)

  $effect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    const id = setInterval(() => {
      bylineIndex = (bylineIndex + 1) % BYLINES.length
    }, BYLINE_ROTATION_MS)
    return () => clearInterval(id)
  })

  function csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
  }

  onMount(async () => {
    try {
      const res = await fetch('/triggers.json', {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      })
      if (!res.ok) {
        error = `Failed to load triggers (${res.status})`
        return
      }
      const data = await res.json()
      triggers = data.triggers
    } catch {
      error = 'Network error — please reload.'
    } finally {
      loading = false
    }
  })

  $effect(() => {
    if (!loading && !explainerInitialized) {
      explainerInitialized = true
      explainerOpen = triggers.length === 0
    }
  })

  let explainerEl: HTMLElement | undefined = $state()
  let highlightDone = false

  $effect(() => {
    if (!loading && explainerEl && !highlightDone) {
      highlightDone = true
      tick().then(() => highlightAll(explainerEl as HTMLElement))
    }
  })
</script>

<div class="trig-list">
  {#if !loading && !error}
    <!-- All bylines stack in the same grid cell so the container
         auto-sizes to the tallest one and the cross-fade is smooth
         without absolute-positioning hacks or min-height guesswork. -->
    <div class="trig-byline-rotator" aria-label="Triggers tagline">
      {#each BYLINES as byline, i}
        <p class="trig-byline" class:trig-byline--active={i === bylineIndex} aria-hidden={i !== bylineIndex}>
          <strong class="trig-byline__hook">{byline.hook}</strong>
          <span class="trig-byline__body">{byline.body}</span>
        </p>
      {/each}
    </div>
  {/if}

  {#if !loading && !error && triggers.length === 0}
    <div class="trig-empty">
      <p>No triggers yet. Create your first trigger to get started.</p>
      <a href="/triggers/new" class="trig-btn trig-btn--primary">New Trigger</a>
    </div>
  {/if}

  {#if !loading && !error}
    <details class="trig-explainer" bind:open={explainerOpen}>
      <summary class="trig-explainer__summary">
        <span class="trig-explainer__title">How triggers work</span>
      </summary>
      <div class="trig-explainer__body" bind:this={explainerEl}>
        <p>
          Triggers turn external events into SMS drafts that your reviewer approves before
          sending. Pick the source that fits — no system to integrate? Forward an email or
          watch the weather. Already have automation? Use the webhook.
        </p>
        <ol class="trig-explainer__steps">
          <li><strong>Create a trigger</strong> — name it, pick the group to message, choose a reviewer, and describe the kind of message you want.</li>
          <li>
            <strong>Connect your source</strong> — pick one:
            <ul class="trig-explainer__sources">
              <li><strong>Forwarded email</strong> — get a unique address like <code class="trig-inline-code">trigger+abc123@{inboundEmailDomain}</code>. Forward any alert email (school closure, monitoring alarm, fundraising milestone) and we'll draft from it. No setup beyond an optional sender allowlist. <a href="/docs/triggers/forward-an-email" class="trig-explainer__learn-more">Learn more →</a></li>
              <li><strong>Weather alert</strong> — pick your ZIPs and the National Weather Service events you care about (Winter Storm Warning, Tornado Warning, Heat Advisory, etc.). We poll NWS every 5 minutes and draft when a matching alert hits. <a href="/docs/triggers/connect-weather-alerts" class="trig-explainer__learn-more">Learn more →</a></li>
              <li><strong>Webhook</strong> — point any system at your trigger's unique URL with a small JSON payload. Signed with HMAC-SHA256 (details below). Works with Zapier, Make.com, n8n, or anything that can POST.</li>
            </ul>
          </li>
          <li><strong>We draft, you approve</strong> — within seconds your reviewer gets a text with a link to the draft. Edit if needed, tap Send.</li>
        </ol>
        <hr class="trig-explainer__divider" />
        <p class="trig-explainer__heading">Webhook source — request format</p>
        <p>
          Only the <strong>webhook</strong> source needs this. Forwarded email and weather triggers
          have no signing or payload format to manage.
        </p>
        <pre class="trig-explainer__code"><code class="language-http">POST https://app.txtsignal.io/webhooks/triggers/&lt;your-trigger-id&gt;
Content-Type: application/json
X-Txtsignal-Timestamp: &lt;unix seconds&gt;
X-Txtsignal-Signature: sha256=&lt;hex hmac, see below&gt;

&#123; "anything": "you want", "we": "pass it to the prompt" &#125;</code></pre>

        <p class="trig-explainer__heading">Signing the request</p>
        <p>
          We require every webhook to be signed so we can confirm it really came from
          your system and the body hasn't been altered in flight. If you're using an
          integration platform like Zapier, Make.com, or n8n, look for their built-in
          <strong>HMAC-SHA256</strong> helper and feed it your signing secret.
        </p>
        <p>
          To sign by hand, you compute an HMAC-SHA256 hash of this exact string:
        </p>
        <pre class="trig-explainer__code"><code>&lt;timestamp&gt;.&lt;raw-request-body&gt;</code></pre>
        <p>
          using your trigger's signing secret as the HMAC key. Send the result as a
          lowercase hex digest, prefixed with <code class="trig-inline-code">sha256=</code>.
          The timestamp must be the current Unix time in seconds — we reject anything more than
          five minutes off to prevent replay attacks.
        </p>

        <p class="trig-explainer__heading">Worked example</p>
        <pre class="trig-explainer__code"><code>signing_secret = "shh-this-is-secret-do-not-share"
timestamp      = "1748025600"
body           = &#123;"event":"snow_day","district":"riverside"&#125;
string_to_sign = "1748025600.&#123;\"event\":\"snow_day\",\"district\":\"riverside\"&#125;"

HMAC-SHA256(string_to_sign, signing_secret)
  → "1a9c…hexdigest…"

X-Txtsignal-Signature: sha256=1a9c…hexdigest…</code></pre>

        <p class="trig-explainer__heading">Code samples</p>
        <p class="trig-explainer__code-label">Ruby</p>
        <pre class="trig-explainer__code"><code class="language-ruby">require 'openssl'
require 'net/http'
require 'json'

body      = &#123; event: "snow_day", district: "riverside" &#125;.to_json
timestamp = Time.now.to_i.to_s
sig       = OpenSSL::HMAC.hexdigest("SHA256", ENV["SIGNING_SECRET"],
                                    "#&#123;timestamp&#125;.#&#123;body&#125;")

Net::HTTP.post(URI("https://app.txtsignal.io/webhooks/triggers/&lt;id&gt;"),
  body,
  "Content-Type"          =&gt; "application/json",
  "X-Txtsignal-Timestamp" =&gt; timestamp,
  "X-Txtsignal-Signature" =&gt; "sha256=#&#123;sig&#125;")</code></pre>

        <p class="trig-explainer__code-label">Python</p>
        <pre class="trig-explainer__code"><code class="language-python">import hmac, hashlib, json, time, os, requests

body      = json.dumps(&#123;"event": "snow_day", "district": "riverside"&#125;)
timestamp = str(int(time.time()))
sig       = hmac.new(os.environ["SIGNING_SECRET"].encode(),
                     f"&#123;timestamp&#125;.&#123;body&#125;".encode(),
                     hashlib.sha256).hexdigest()

requests.post("https://app.txtsignal.io/webhooks/triggers/&lt;id&gt;",
  data=body,
  headers=&#123;
    "Content-Type":          "application/json",
    "X-Txtsignal-Timestamp": timestamp,
    "X-Txtsignal-Signature": f"sha256=&#123;sig&#125;",
  &#125;)</code></pre>

        <p class="trig-explainer__code-label">Node.js</p>
        <pre class="trig-explainer__code"><code class="language-javascript">import crypto from "node:crypto"

const body      = JSON.stringify(&#123; event: "snow_day", district: "riverside" &#125;)
const timestamp = Math.floor(Date.now() / 1000).toString()
const sig       = crypto
  .createHmac("sha256", process.env.SIGNING_SECRET)
  .update(`$&#123;timestamp&#125;.$&#123;body&#125;`)
  .digest("hex")

await fetch("https://app.txtsignal.io/webhooks/triggers/&lt;id&gt;", &#123;
  method:  "POST",
  body,
  headers: &#123;
    "Content-Type":          "application/json",
    "X-Txtsignal-Timestamp": timestamp,
    "X-Txtsignal-Signature": `sha256=$&#123;sig&#125;`,
  &#125;,
&#125;)</code></pre>

        <p class="trig-explainer__footnote">
          Your unique URL and signing secret live on each trigger's edit page.
        </p>
      </div>
    </details>
  {/if}

  {#if loading}
    <div class="trig-loading" aria-busy="true">
      <div class="trig-spinner"></div>
      <p>Loading triggers...</p>
    </div>
  {:else if error}
    <div class="trig-error" role="alert">{error}</div>
  {:else if triggers.length > 0}
    <table class="trig-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Groups</th>
          <th>Reviewer</th>
          <th>Status</th>
          <th>Recent Activity</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each triggers as trigger (trigger.id)}
          <tr class="trig-row" onclick={() => window.location.assign(`/triggers/${trigger.id}/edit`)}>
            <td class="trig-col--name">{trigger.name}</td>
            <td>
              <span class="source-badge source-badge-{trigger.source_type}">
                {#if trigger.source_type === 'webhook'}
                  <svg class="source-badge__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>
                  </svg>Webhook
                {:else if trigger.source_type === 'weather'}
                  <svg class="source-badge__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M17 18a4 4 0 0 0 0-8 6 6 0 0 0-11.7 1.6A4.5 4.5 0 0 0 6 19h11Z"/>
                  </svg>Weather
                {:else if trigger.source_type === 'email'}
                  <svg class="source-badge__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" d="M3 6h18v12H3z"/>
                    <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" d="m3 6 9 7 9-7"/>
                  </svg>Email
                {/if}
              </span>
            </td>
            <td title={trigger.target_group_names?.join(', ') ?? ''}>{formatGroupList(trigger.target_group_names)}</td>
            <td>{trigger.reviewer_email ?? '—'}</td>
            <td>
              <span class="trig-badge" class:trig-badge--active={trigger.active} class:trig-badge--inactive={!trigger.active}>
                {trigger.active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td class="trig-col--activity" title={trigger.last_fire_at ?? ''}>{timeAgo(trigger.last_fire_at)}</td>
            <td class="trig-col--actions">
              <a
                href={`/triggers/${trigger.id}/edit`}
                class="trig-row-chevron"
                aria-label="Edit {trigger.name}"
                onclick={(e) => e.stopPropagation()}
              >›</a>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>

<style lang="scss">
  .trig-list {
    padding: 1rem 0;
  }

  /* Rotating byline — the marketing element. Grid stacks all bylines
     in the same cell; container auto-sizes to the tallest. Only the
     active one has opacity:1; others fade in/out smoothly. */
  .trig-byline-rotator {
    display: grid;
    max-width: 60rem;
    margin: 0 0 1rem;
  }

  .trig-byline {
    grid-column: 1;
    grid-row: 1;
    margin: 0;
    font-size: 1.02rem;
    line-height: 1.5;
    color: #555;
    opacity: 0;
    transition: opacity 600ms ease-in-out;
    pointer-events: none;
  }

  .trig-byline--active {
    opacity: 1;
    pointer-events: auto;
  }

  .trig-byline__hook {
    font-weight: 600;
    color: #1a1a1a;
    margin-right: 0.25rem;
  }

  /* Honor user motion preference — disable rotation & remove transition. */
  @media (prefers-reduced-motion: reduce) {
    .trig-byline { transition: none; }
  }

  /* Explainer — collapsed it's a thin link; only puts on the panel
     dressing when actually expanded. Keeps the page quiet for users
     who already know what triggers are. */
  .trig-explainer {
    background: transparent;
    border: 0;
    border-radius: 8px;
    margin-bottom: 1.25rem;
    transition: background-color 0.15s, border-color 0.15s;
  }
  .trig-explainer[open] {
    background: #f7f9fc;
    border: 1px solid #e3e8ef;
  }

  .trig-explainer__summary {
    cursor: pointer;
    padding: 0.5rem 0.75rem;
    list-style: none;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 8px;

    &::-webkit-details-marker { display: none; }

    &:hover { background: #f0f3f8; }

    &::before {
      content: '▸';
      color: #888;
      font-size: 0.85rem;
      transition: transform 0.15s;
    }
  }

  details[open] .trig-explainer__summary::before {
    transform: rotate(90deg);
  }

  .trig-explainer__title {
    font-weight: 500;
    color: #4a6480;
    font-size: 0.88rem;
  }

  .trig-explainer__body {
    padding: 0 1.1rem 1rem;
    color: #444;
    font-size: 0.9rem;
    line-height: 1.5;

    p { margin: 0.6rem 0; }
  }

  .trig-explainer__steps {
    margin: 0.5rem 0 0.75rem 1.2rem;
    padding: 0;

    li { margin-bottom: 0.4rem; }
  }

  .trig-explainer__sources {
    margin: 0.4rem 0 0.6rem 1.1rem;
    padding: 0;
    list-style: disc;

    li { margin-bottom: 0.35rem; line-height: 1.45; }
  }

  .trig-explainer__learn-more {
    color: #4a90e2;
    text-decoration: none;
    font-weight: 500;
    white-space: nowrap;

    &:hover { text-decoration: underline; }
  }

  .trig-explainer__divider {
    border: 0;
    border-top: 1px solid #e0e4ea;
    margin: 1.2rem 0 0.6rem;
  }

  .trig-explainer__heading {
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0.85rem !important;
    margin-bottom: 0.25rem !important;
  }

  .trig-explainer__code {
    background: #f1f3f6;
    border: 1px solid #e0e4ea;
    border-radius: 6px;
    padding: 0.7rem 0.9rem;
    font-size: 0.78rem;
    line-height: 1.45;
    color: #2c3e50;
    overflow-x: auto;
    margin: 0.3rem 0 0.6rem;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
  }

  .trig-explainer__code-label {
    font-size: 0.78rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5a6c80;
    font-weight: 600;
    margin-top: 0.6rem !important;
    margin-bottom: 0.15rem !important;
  }

  .trig-inline-code {
    background: #eef1f5;
    border: 1px solid #e0e4ea;
    border-radius: 3px;
    padding: 0.05rem 0.3rem;
    font-size: 0.82em;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    color: #2c3e50;
  }

  .trig-explainer__footnote {
    color: #666;
    font-size: 0.85rem !important;
    font-style: italic;
  }

  .trig-list__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .trig-list__title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: #1a1a1a;
  }

  .trig-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem 0;
    color: #888;

    p { margin: 0; font-size: 0.9rem; }
  }

  .trig-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #e0e0e0;
    border-top-color: #4a90e2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .trig-error {
    background: #fdecea;
    border: 1px solid #ef9a9a;
    border-radius: 8px;
    color: #c62828;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .trig-empty {
    text-align: center;
    padding: 3rem 1rem;
    color: #666;

    p { margin-bottom: 1rem; }
  }

  .trig-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;

    th {
      text-align: left;
      padding: 0.6rem 0.75rem;
      border-bottom: 2px solid #e5e5e5;
      color: #555;
      font-weight: 600;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
  }

  .trig-row {
    cursor: pointer;
    transition: background 0.1s;

    &:hover { background: #f7f9fc; }

    td {
      padding: 0.75rem;
      border-bottom: 1px solid #f0f0f0;
      vertical-align: middle;
    }
  }

  .trig-col--name {
    font-weight: 500;
    color: #1a1a1a;
  }

  .trig-col--actions {
    text-align: right;
    white-space: nowrap;
  }

  .trig-badge {
    display: inline-block;
    padding: 0.2em 0.55em;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  /* Use :global for dynamically toggled modifier classes */
  :global(.trig-badge--active)   { background: #edf7ed; color: #2e7d32; }
  :global(.trig-badge--inactive) { background: #fafafa; color: #888; border: 1px solid #ddd; }

  .trig-link {
    color: #4a90e2;
    text-decoration: none;
    font-size: 0.85rem;

    &:hover { text-decoration: underline; }
  }

  .trig-btn {
    display: inline-block;
    padding: 0.55rem 1.1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    text-decoration: none;
    transition: opacity 0.15s;

    &:hover { opacity: 0.88; }
  }

  .trig-btn--primary {
    background: #4a90e2;
    color: #fff;
  }

  /* Monochrome source badges — same neutral chip for all three;
     the icon does the type-distinguishing work. Cuts the Skittles
     row when an account mixes weather/email/webhook triggers and
     lets the green Active badge win the row's color attention. */
  .source-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.15rem 0.55rem 0.15rem 0.45rem;
    border-radius: 10px;
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    vertical-align: middle;
    background: #eef0f3;
    color: #4a5566;
  }

  .source-badge__icon {
    width: 0.95rem;
    height: 0.95rem;
    flex-shrink: 0;
    display: block;
  }

  /* Right-aligned, muted relative-time column. */
  .trig-col--activity {
    color: #777;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  /* Edit affordance — a quiet chevron at the row end. The whole
     row is already clickable; this is just the visual hint that
     it goes somewhere. */
  .trig-row-chevron {
    display: inline-block;
    color: #bbb;
    text-decoration: none;
    font-size: 1.4rem;
    line-height: 1;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    transition: color 0.12s, background-color 0.12s;
  }
  .trig-row:hover .trig-row-chevron { color: #4a90e2; }
  .trig-row-chevron:hover,
  .trig-row-chevron:focus-visible {
    color: #4a90e2;
    background: #eef4fb;
    outline: none;
  }
</style>
